import { useState, useMemo, useCallback } from "react";
import { createFileRoute, Link, type FileRouteTypes } from "@tanstack/react-router";
import { type SortingState } from "@tanstack/react-table";
import { useAnalyticsEvents, type AnalyticsEventsParams } from "@/hooks/use-admin-analytics";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { EventFilters } from "./event-filters";
import { useEventColumns } from "./event-columns";

export const Route = createFileRoute("/_admin/analytics/events")({
  validateSearch: (search: Record<string, unknown>) => ({
    since: typeof search.since === "string" ? search.since : undefined,
  }),
  component: AnalyticsEventsPage,
});

const PAGE_SIZE = 20;

function computeSince(preset: string): string | undefined {
  if (preset === "all") return undefined;
  const now = Date.now();
  const ms: Record<string, number> = {
    "1h": 60 * 60 * 1000,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
  };
  const delta = ms[preset];
  if (!delta) return undefined;
  return new Date(now - delta).toISOString();
}

function AnalyticsEventsPage() {
  const { since: searchSince } = Route.useSearch();

  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [platform, setPlatform] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [browser, setBrowser] = useState("");
  const [sincePreset, setSincePreset] = useState(searchSince ?? "all");
  const [since, setSince] = useState<string | undefined>(
    searchSince ? computeSince(searchSince) : undefined,
  );
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  const debouncedSearch = useDebounce(search, 300);
  const columns = useEventColumns();

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      resetPage();
    },
    [resetPage],
  );

  const handleEventTypeChange = useCallback(
    (value: string) => {
      setEventType(value);
      resetPage();
    },
    [resetPage],
  );

  const handlePlatformChange = useCallback(
    (value: string) => {
      setPlatform(value);
      resetPage();
    },
    [resetPage],
  );

  const handleSincePresetChange = useCallback(
    (value: string) => {
      setSincePreset(value);
      setSince(computeSince(value));
      resetPage();
    },
    [resetPage],
  );

  const handleDeviceTypeChange = useCallback(
    (value: string) => {
      setDeviceType(value);
      resetPage();
    },
    [resetPage],
  );

  const handleBrowserChange = useCallback(
    (value: string) => {
      setBrowser(value);
      resetPage();
    },
    [resetPage],
  );

  const params: AnalyticsEventsParams = useMemo(() => {
    const p: AnalyticsEventsParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (eventType) p.event_type = eventType;
    if (platform) p.platform = platform;
    if (since) p.since = since;
    if (deviceType) p.device_type = deviceType;
    if (browser) p.browser = browser;
    if (sorting.length > 0 && sorting[0]) {
      p.sort = sorting[0].id as AnalyticsEventsParams["sort"];
      p.order = sorting[0].desc ? "desc" : "asc";
    }
    return p;
  }, [debouncedSearch, eventType, platform, since, deviceType, browser, page, sorting]);

  const { data, isLoading } = useAnalyticsEvents(params);
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Cast needed because the analytics index route is a sibling directory route
  const analyticsRoute = "/analytics" as FileRouteTypes["to"];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to={analyticsRoute} className="text-sm text-slate-500 hover:text-slate-700">
          &larr; Analytics
        </Link>
        <h1 className="text-2xl font-bold text-slate-900">Analytics Events</h1>
      </div>

      <EventFilters
        search={search}
        onSearchChange={handleSearchChange}
        eventType={eventType}
        onEventTypeChange={handleEventTypeChange}
        platform={platform}
        onPlatformChange={handlePlatformChange}
        sincePreset={sincePreset}
        onSincePresetChange={handleSincePresetChange}
        deviceType={deviceType}
        onDeviceTypeChange={handleDeviceTypeChange}
        browser={browser}
        onBrowserChange={handleBrowserChange}
      />

      <DataTable
        columns={columns}
        data={data?.events ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No analytics events found"
      />

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
